import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { useVariantLensApiClient } from '../features/auth/hooks/useVariantLensApiClient'
import IgvMount, { type IgvReference, type IgvTrack } from '../features/viewers/igv/IgvMount'
import { DEFAULT_IGV_SETTINGS, toIgvTrack, type ApiTrack } from './igvTracks'

/**
 * Tracks-only order viewer (GEN-5467).
 *
 * Opens an order resolved from the catalog via GET /orders/{token}/tracks, which
 * returns IGV tracks plus an optional reference. Deliberately narrower than
 * CasePage: the order path yields alignment/variant tracks but no case-scoped
 * variant table (that endpoint is keyed on a sample/case id the order path does
 * not provide), so this renders the IGV view only. Reviewers who need the
 * variant table open the case by its sample id.
 */

type ApiReference = {
  id: string
  fastaUrl: string
  indexUrl: string
}

type OrderTracksResponse = {
  order_id?: string
  tracks?: ApiTrack[]
  reference?: ApiReference | null
}

// Same custom-reference labels as CasePage; both builds are GRCh38 and the LSMC
// CRAMs use the no-alt analysis set (GEN-5234). Kept local to avoid widening
// CasePage's surface for this narrower page.
function referenceDisplayName(id: string): string {
  switch (id) {
    case 'hg38_no_alt':
      return 'GRCh38 (no-alt)'
    case 'hg38_broad':
      return 'GRCh38 (Broad)'
    default:
      return 'GRCh38'
  }
}

// A minimum interval between presign refreshes, so a genuinely broken URL can't
// spin the fetch effect. Mirrors CasePage's guard, scaled down for this simpler
// page.
const REFRESH_MIN_INTERVAL_MS = 30_000

// Match only genuine presign-expiry signals, identical to CasePage: S3 returns
// 403 / Forbidden / Access Denied once a presign expires. Deliberately scoped to
// those word-boundary S3-auth signals so an unrelated igv.js error cannot trigger
// a spurious presign refresh.
function looksLikeExpiredUrl(message: string): boolean {
  return /\b(403|forbidden|access denied)\b/i.test(message)
}

const OrderViewerPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const orderToken = token ? decodeURIComponent(token) : ''
  const apiClient = useVariantLensApiClient()

  const [loading, setLoading] = useState(true)
  const [tracks, setTracks] = useState<ApiTrack[] | null>(null)
  const [reference, setReference] = useState<ApiReference | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Bump to re-run the fetch and get fresh presigned URLs (GEN-5243), guarded so
  // a broken URL cannot loop.
  const [refreshNonce, setRefreshNonce] = useState(0)
  const lastRefreshRef = useRef(0)
  // The token the current tracks are being shown/loaded for. Claimed
  // synchronously when a real load starts, so a bumped refreshNonce only means
  // "refresh in place" when this ref still matches the token being loaded. Guards
  // against a token change (react-router reuses this component instance for a
  // param-only nav) being mistaken for a presign refresh, which would otherwise
  // keep the prior order's tracks and swallow the new order's errors (PR #106
  // review). Mirrors CasePage's tracksKeyRef.
  const tokenRef = useRef<string | null>(null)
  const refresh = useCallback(() => {
    const now = Date.now()
    if (now - lastRefreshRef.current < REFRESH_MIN_INTERVAL_MS) return
    lastRefreshRef.current = now
    setRefreshNonce((n) => n + 1)
  }, [])

  useEffect(() => {
    if (!orderToken) {
      setLoading(false)
      setError('No order specified')
      return
    }
    const abort = new AbortController()
    // A refresh only when the nonce was bumped AND we are still on the same token
    // the current tracks belong to; any token change is a real load.
    const isRefresh = refreshNonce > 0 && tokenRef.current === orderToken
    if (!isRefresh) {
      // Claim the token synchronously as the real load starts, so a later
      // refresh can't be attributed to a different order.
      tokenRef.current = orderToken
      setLoading(true)
      setTracks(null)
      setReference(null)
    }
    setError(null)
    const url = `/orders/${encodeURIComponent(orderToken)}/tracks`
    apiClient
      .get(url, undefined, true, abort.signal)
      .then(async (response) => {
        if (response.status === 404) throw new Error('Order not found or has no delivered files')
        if (response.status === 503) throw new Error('Order lookup is temporarily unavailable')
        if (!response.ok) throw new Error(`API returned ${response.status}`)
        const body = (await response.json()) as OrderTracksResponse
        setTracks(body.tracks ?? [])
        setReference(body.reference ?? null)
      })
      .catch((e: unknown) => {
        if (abort.signal.aborted) return
        // On a refresh, keep the last-good tracks on screen rather than blanking.
        if (!isRefresh) setError(e instanceof Error ? e.message : 'Failed to load order')
        else lastRefreshRef.current = 0
      })
      .finally(() => {
        if (!abort.signal.aborted && !isRefresh) setLoading(false)
      })
    return () => abort.abort()
  }, [orderToken, apiClient, refreshNonce])

  const igvTracks = useMemo<IgvTrack[] | undefined>(
    () => (tracks ? tracks.map((t) => toIgvTrack(t, DEFAULT_IGV_SETTINGS)) : undefined),
    [tracks]
  )
  const igvReference = useMemo<IgvReference | undefined>(
    () =>
      reference
        ? {
            id: reference.id,
            name: referenceDisplayName(reference.id),
            fastaURL: reference.fastaUrl,
            indexURL: reference.indexUrl,
          }
        : undefined,
    [reference]
  )

  const onIgvError = useCallback(
    (message: string) => {
      if (looksLikeExpiredUrl(message)) refresh()
    },
    [refresh]
  )

  return (
    <div className="flex h-full flex-col">
      <header className="border-border-subtle flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-text-heading font-mono text-sm">{orderToken}</span>
          <span className="text-text-subtle text-xs">Order view (IGV tracks)</span>
        </div>
        <Link to="/home" className="text-brand-700 hover:text-brand-800 text-sm underline underline-offset-2">
          Back to orders
        </Link>
      </header>

      <div className="min-h-0 flex-1">
        {loading && (
          <p data-testid="order-viewer-loading" className="text-text-subtle p-6 text-sm">
            Loading order...
          </p>
        )}
        {!loading && error && (
          <p data-testid="order-viewer-error" role="alert" className="text-error-600 p-6 text-sm">
            {error}
          </p>
        )}
        {!loading && !error && igvTracks && igvTracks.length === 0 && (
          <p data-testid="order-viewer-empty" className="text-text-subtle p-6 text-sm">
            This order has no viewable tracks.
          </p>
        )}
        {!loading && !error && igvTracks && igvTracks.length > 0 && (
          <IgvMount
            tracks={igvTracks}
            genome="hg38"
            reference={igvReference}
            onError={onIgvError}
            reloadKey={orderToken}
            data-testid="order-viewer-igv"
            className="h-full"
            hostClassName="min-h-full"
          />
        )}
      </div>
    </div>
  )
}

export default OrderViewerPage
